/**
 * Variant Configurator (SCH001R-85 stitching -> native variants rework)
 *
 * Presents the configurator UI (size swiper, fabric tabs, leg grid, mobile
 * bottom-sheet modals) on top of NATIVE Shopify variants.
 *
 * Selection flow:
 * 1. A click on an option card checks the matching radio inside the hidden
 *    <product-variant-selects> and dispatches a change event.
 * 2. assets/product-variants.js (ProductInfo) picks it up, fetches the section
 *    through the Section Rendering API (?option_values=...) and natively
 *    updates price, SKU, buy buttons, quantity and the variant id input.
 * 3. ProductInfo publishes PUB_SUB_EVENTS.variantChange with the fetched HTML.
 *    This class consumes it: re-reads #variant-configurator-data (which
 *    carries the NEW variant's custom.gallery), rebuilds the gallery, syncs
 *    option availability/selected states, accordion headers, mobile bar,
 *    selection sentence, technical specification and per-variant text blocks.
 *
 * >250-variant safe: no product.variants JSON is ever inlined. Per-variant
 * data always arrives with the section render for the selected variant only.
 */

// Custom delegate helpers (shared contract with stitching-options.js)
if (!Element.prototype.customDelegate) {
  Element.prototype.customDelegate = function (eventType, selector, handler) {
    this.addEventListener(eventType, function (event) {
      const target = event.target.closest(selector);
      if (target && this.contains(target)) {
        handler.call(this, event, target);
      }
    });
  };
}

if (!Document.prototype.customDelegate) {
  Document.prototype.customDelegate = function (eventType, selector, handler) {
    this.addEventListener(eventType, function (event) {
      const target = event.target.closest(selector);
      if (target) {
        handler.call(this, event, target);
      }
    });
  };
}

// Bottom-sheet modal, identical behaviour to the stitching implementation.
// Defined here as well so variant products work without stitching-options.js.
if (typeof window.CustomModal === 'undefined') {
  class CustomModal {
    constructor(modal, selectors, closeCallback = () => {}, openCallback = () => {}) {
      this.modal = modal;
      this.selectors = {
        scrollWrapper: '.js-custom-modal-scroll-wrapper',
        openers: '.js-custom-modal-open',
        closures: '.js-custom-modal-close',
        bottomSheetHandle: '.js-custom-modal-bottom-sheet-handle',
        ...selectors
      };
      this.closeCallback = closeCallback;
      this.openCallback = openCallback;
      this.isShown = false;
      this.init();
    }

    init() {
      if (!this.modal) return null;
      this.handleOpen = this.open.bind(this);
      this.handleClose = this.close.bind(this);
      this.scrollWrapper = this.modal.querySelector(this.selectors.scrollWrapper);
      this.bottomSheetHandle = this.modal.querySelector(this.selectors.bottomSheetHandle);
      this.attachListeners();
    }

    open(e, target) {
      if (e) e.preventDefault();
      this.isShown = true;
      this.modal.classList.add('is-shown');
      document.body.style.overflow = 'hidden';
      this.openCallback(target);
    }

    close(e, target) {
      if (e) {
        if (!target || !target.hasAttribute('data-without-preventing')) {
          e.preventDefault();
        }
      }
      this.isShown = false;
      this.modal.classList.remove('is-shown');
      if (this.scrollWrapper) {
        this.scrollWrapper.style.transform = '';
      }
      document.body.style.overflow = '';
      this.closeCallback(target);
    }

    startDrag(event, clientY) {
      this.isDragging = true;
      this.startY = clientY;
      this.modal.classList.add('is-dragging');
      event.preventDefault();
    }

    drag(event, clientY) {
      if (!this.isDragging) return null;
      const deltaY = clientY - this.startY;
      if (deltaY > 0) {
        this.scrollWrapper.style.transform = `translateY(${deltaY}px)`;
      } else {
        this.scrollWrapper.style.transform = 'translateY(0px)';
      }
      event.preventDefault();
    }

    endDrag(event, clientY) {
      if (!this.isDragging) return null;
      const deltaY = clientY - this.startY;
      this.modal.classList.remove('is-dragging');
      if (deltaY > 80) {
        this.close();
      } else {
        this.scrollWrapper.style.transform = 'translateY(0px)';
      }
      this.isDragging = false;
    }

    attachListeners() {
      this.modal.customDelegate('click', this.selectors.closures, this.handleClose);
      if (this.selectors.openers) {
        document.documentElement.customDelegate('click', this.selectors.openers, this.handleOpen);
      }
      if (this.modal.dataset.fullscreenModal !== 'true') {
        this.modal.customDelegate('click', this.selectors.scrollWrapper, (e, target) => {
          if (e.target !== target) return null;
          this.handleClose(e, target);
        });
      }
      if (this.bottomSheetHandle) {
        this.bottomSheetHandle.addEventListener('mousedown', (event) => {
          this.startDrag(event, event.clientY);
        });
        document.addEventListener('mousemove', (event) => {
          this.drag(event, event.clientY);
        });
        document.addEventListener('mouseup', (event) => {
          this.endDrag(event, event.clientY);
        });
        this.bottomSheetHandle.addEventListener('touchstart', (event) => {
          this.startDrag(event, event.touches[0].clientY);
        }, { passive: false });
        document.addEventListener('touchmove', (event) => {
          this.drag(event, event.touches[0].clientY);
        }, { passive: false });
        document.addEventListener('touchend', (event) => {
          if (event.changedTouches.length > 0) {
            this.endDrag(event, event.changedTouches[0].clientY);
          }
        });
      }
    }
  }

  window.CustomModal = CustomModal;
}

class VariantConfigurator {
  constructor() {
    this.mobileModals = {};
    this.pendingFabricAutoSelect = null;
    this.data = this.loadData(document);
    if (!this.data) return;

    this.container = document.querySelector('[data-variant-configurator]');
    if (!this.container) return;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initializeSelectionDisplay();
    this.initializeSizeSwiper();
    this.initializeMobileModals();
    this.activateSelectedFabricTab();
    this.initShopPayButton();
    this.syncCartProperties();

    const initialDelay = this.isMobile() ? 100 : 10;
    setTimeout(() => {
      this.updateAccordionHeaders();
      this.updateMobileDisplay();
      this.updateSelectionDisplay();
      this.updateSubmitButton();
      this.updateStickyBar();
    }, initialDelay);

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));

      // A completed add closes the set: the next one gets a fresh id, matching
      // the stitching behaviour of one set id per add.
      subscribe(PUB_SUB_EVENTS.cartUpdate, () => {
        this.stitchingSetId = null;
        this.syncCartProperties();
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /* Data                                                                 */
  /* ------------------------------------------------------------------ */

  loadData(rootDocument) {
    const script = rootDocument.querySelector('#variant-configurator-data');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[variant-configurator] Failed to parse data blob', error);
      return null;
    }
  }

  getOption(role) {
    return this.data.options.find((option) => option.role === role) || null;
  }

  getValue(role, valueId) {
    const option = this.getOption(role);
    if (!option) return null;
    return option.values.find((value) => value.id.toString() === valueId.toString()) || null;
  }

  getSelectedValue(role) {
    const option = this.getOption(role);
    if (!option) return null;
    return option.values.find((value) => value.selected) || null;
  }

  /* ------------------------------------------------------------------ */
  /* Events                                                               */
  /* ------------------------------------------------------------------ */

  bindEvents() {
    document.addEventListener('click', (e) => {
      const option = e.target.closest('[data-configurator-option]');
      if (option && !option.classList.contains('disabled')) {
        this.handleOptionClick(option);
        return;
      }

      const mobileTrigger = e.target.closest('.js-stitching-mobile-trigger');
      if (mobileTrigger) {
        e.preventDefault();
        this.handleMobileTriggerClick(mobileTrigger);
        return;
      }

      const tabButton = e.target.closest('.fabric-tabs__nav-item');
      if (tabButton) {
        e.preventDefault();
        this.handleTabClick(tabButton);
        return;
      }

      const accordionToggle = e.target.closest('[data-accordion-toggle]');
      if (accordionToggle) {
        e.preventDefault();
        this.handleAccordionToggle(accordionToggle);
      }
    });

    // Quantity selector (theme.quantity metafield): the button price follows
    // quantity x unit price. Delegated because the input is re-rendered by
    // the Section Rendering API on every variant change.
    document.addEventListener('change', (e) => {
      if (e.target.matches('product-info .product-form__quantity .quantity__input')) {
        this.updateSubmitButton();
      }
    });

    // Back navigation from the cart restores the page from bfcache with the
    // quantity input's last value but the button text frozen at whatever it
    // showed when the page was left. Recompute once form state has settled
    // (non-bfcache restores repopulate inputs around the load event too).
    window.addEventListener('pageshow', () => {
      setTimeout(() => this.updateSubmitButton(), 100);
    });
  }

  getNativePicker() {
    // Always resolve fresh: the Section Rendering API replaces the node on
    // every variant change (HTMLUpdateUtility inserts the new node before the
    // old one, so the first match is the live picker).
    return document.querySelector('product-info product-variant-selects')
      || document.querySelector('product-variant-selects');
  }

  handleOptionClick(optionElement) {
    const role = optionElement.dataset.optionRole;
    const valueId = optionElement.dataset.optionValueId;
    if (!role || !valueId) return;

    const picker = this.getNativePicker();
    if (!picker) return;

    const input = picker.querySelector(`input[data-option-value-id="${valueId}"]`);
    if (!input) return;

    // Optimistic UI: mark selection locally, the authoritative state arrives
    // with the section re-render.
    this.markSelected(role, valueId);
    const value = this.getValue(role, valueId);
    if (value) {
      const option = this.getOption(role);
      option.values.forEach((v) => {
        v.selected = v.id.toString() === valueId.toString();
      });
      option.selectedValue = value.name;
    }

    if (role === 'fabric' && value) {
      this.pendingFabricAutoSelect = value.label || value.name;
    }

    if (role === 'size') {
      this.updateSwiperToSelectedOption(optionElement);
    }

    // Close the bottom-sheet modal after a pick.
    const modal = optionElement.closest('.stitching-modal');
    if (modal) {
      const modalType = modal.id.replace('stitching-modal-', '');
      if (this.mobileModals[modalType]) {
        setTimeout(() => {
          this.mobileModals[modalType].close();
        }, 300);
      }
    }

    this.updateAccordionHeaders();
    this.updateMobileDisplay();
    this.updateSelectionDisplay();

    if (!input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    } else if (this.pendingFabricAutoSelect) {
      // Same value re-picked: no re-render will fire, run the drawer hook now.
      this.autoSelectFabricInDrawer(this.pendingFabricAutoSelect);
      this.pendingFabricAutoSelect = null;
    }

    document.dispatchEvent(new CustomEvent('variant-configurator:option-click', {
      detail: { role, valueId, value },
      bubbles: true
    }));
  }

  markSelected(role, valueId) {
    document.querySelectorAll(`[data-configurator-option][data-option-role="${role}"]`).forEach((el) => {
      el.classList.toggle('selected', el.dataset.optionValueId.toString() === valueId.toString());
    });
  }

  /* ------------------------------------------------------------------ */
  /* Variant change (Section Rendering API response)                      */
  /* ------------------------------------------------------------------ */

  onVariantChange(event) {
    const { html, variant } = event.data || {};
    if (!html) return;

    const freshData = this.loadData(html);
    if (!freshData || freshData.productId !== this.data.productId) return;

    this.data = freshData;

    this.syncOptionStates();
    this.updateAccordionHeaders();
    this.updateMobileDisplay();
    this.updateSelectionDisplay();
    this.updateSubmitButton();
    this.syncCartProperties();
    this.updateGalleryFromData();
    this.updateStickyBar();
    this.updateTechnicalSpecification(html);
    this.updateVariantTextBlocks(html);

    // Legacy bridge: parts of the theme (sticky form) listen to this event.
    if (variant) {
      document.dispatchEvent(new CustomEvent('variant:change', {
        detail: { variant },
        bubbles: true
      }));
    }

    if (this.pendingFabricAutoSelect) {
      this.autoSelectFabricInDrawer(this.pendingFabricAutoSelect);
      this.pendingFabricAutoSelect = null;
    }
  }

  syncOptionStates() {
    document.querySelectorAll('[data-configurator-option]').forEach((el) => {
      const value = this.getValue(el.dataset.optionRole, el.dataset.optionValueId);
      if (!value) return;
      el.classList.toggle('selected', !!value.selected);
      // variantId null = this value has no variant against the currently
      // selected other options (sparse matrix cell) - same rule as the
      // Liquid `value.available and value.variant` disabled condition.
      el.classList.toggle('disabled', !value.available || !value.variantId);

      // Tile price and dimensions follow the current fabric/leg context
      // (the SSR tiles are frozen at first-paint values otherwise).
      const priceEl = el.querySelector('.price-item--regular');
      if (priceEl && value.priceFormatted) {
        priceEl.textContent = value.priceFormatted;
      }
      const dimsEl = el.querySelector('.size-slide-card__dimensions, .stitching-modal__option-details');
      if (dimsEl && value.width && value.height && value.depth) {
        dimsEl.textContent = `W${value.width}cm H${value.height}cm D${value.depth}cm`;
      }
    });
  }

  updateTechnicalSpecification(html) {
    const source = html.querySelector('.product-accordion__content-inner-technical-specifications');
    const destination = document.querySelector('.product-accordion__content-inner-technical-specifications');
    if (source && destination) {
      destination.innerHTML = source.innerHTML;
    }
  }

  updateVariantTextBlocks(html) {
    document.querySelectorAll('.product__metafield_for_variant-block[data-block-id]').forEach((destination) => {
      const blockId = destination.getAttribute('data-block-id');
      const source = html.querySelector(`.product__metafield_for_variant-block[data-block-id="${blockId}"]`);
      if (source) {
        destination.innerHTML = source.innerHTML;
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Fabric drawer auto-select                                            */
  /* ------------------------------------------------------------------ */

  async autoSelectFabricInDrawer(fabricLabel) {
    const fabricDrawer = document.querySelector('fabric-drawer');
    if (!fabricDrawer || !fabricLabel) return;

    // Fabric option values are no longer products: match the sample product
    // in the drawer by title (sample titles start with the fabric name).
    const normalizedLabel = fabricLabel.trim().toLowerCase();
    let variantId = null;

    fabricDrawer.querySelectorAll('.fabric-drawer__item-wrapper').forEach((item) => {
      if (variantId) return;
      const titleEl = item.querySelector('.fabric-drawer__item-content-title');
      const checkbox = item.querySelector('.fabric-drawer__item-checkbox');
      if (!titleEl || !checkbox) return;
      const title = titleEl.textContent.trim().toLowerCase();
      if (title === normalizedLabel || title.startsWith(`${normalizedLabel} -`) || title.startsWith(`${normalizedLabel} –`)) {
        variantId = checkbox.value;
      }
    });

    if (!variantId) return;

    try {
      if (!fabricDrawer.state) {
        fabricDrawer.loadState();
      }
      await fabricDrawer.updateCartSamples();

      if (!fabricDrawer.canAddMore()) {
        fabricDrawer.showMaxSamplesWarning(true);
        return;
      }

      const id = variantId.toString();
      if (fabricDrawer.state.cartSamples.includes(id) || fabricDrawer.state.selectedSamples.includes(id)) {
        return;
      }

      const wasAdded = fabricDrawer.addToSelected(variantId);
      if (wasAdded) {
        fabricDrawer.syncUIWithState();
        fabricDrawer.updateFooterMessage();
      }
    } catch (error) {
      console.error('[variant-configurator] Fabric drawer auto-select failed', error);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Accordions                                                           */
  /* ------------------------------------------------------------------ */

  closeAllOtherAccordions(currentType) {
    document.querySelectorAll('[data-accordion-toggle]').forEach((toggle) => {
      const type = toggle.dataset.accordionToggle;
      if (type === currentType) return;
      const content = document.querySelector(`[data-accordion-content="${type}"]`);
      toggle.setAttribute('aria-expanded', 'false');
      if (content) content.classList.remove('open');
    });
  }

  handleAccordionToggle(accordionToggle) {
    const type = accordionToggle.dataset.accordionToggle;
    const content = document.querySelector(`[data-accordion-content="${type}"]`);
    const isExpanded = accordionToggle.getAttribute('aria-expanded') === 'true';

    if (content) {
      if (isExpanded) {
        accordionToggle.setAttribute('aria-expanded', 'false');
        content.classList.remove('open');
      } else {
        this.closeAllOtherAccordions(type);
        accordionToggle.setAttribute('aria-expanded', 'true');
        content.classList.add('open');
      }
    }

    setTimeout(() => {
      this.updateAccordionHeaders();
    }, 10);

    document.dispatchEvent(new CustomEvent('accordion:toggle', {
      detail: { type: type, isOpen: !isExpanded },
      bubbles: true
    }));
  }

  updateAccordionHeaders() {
    this.data.options.forEach((option) => {
      this.updateAccordionHeader(option.role);
    });
  }

  updateAccordionHeader(role) {
    const defaultInfo = document.querySelector(`[data-accordion-default="${role}"]`);
    const selectedInfo = document.querySelector(`[data-accordion-selected="${role}"]`);
    const selectedImage = document.querySelector(`[data-selected-image="${role}"]`);
    const selectedTitle = document.querySelector(`[data-selected-title="${role}"]`);
    const selectedDetails = document.querySelector(`[data-selected-details="${role}"]`);
    if (!defaultInfo || !selectedInfo) return;

    const value = this.getSelectedValue(role);
    const accordionToggle = document.querySelector(`[data-accordion-toggle="${role}"]`);
    const isOpen = accordionToggle && accordionToggle.getAttribute('aria-expanded') === 'true';

    if (value && !isOpen) {
      defaultInfo.style.display = 'none';
      selectedInfo.classList.remove('hidden');

      if (selectedImage) {
        if (value.image) {
          selectedImage.style.display = 'block';
          selectedImage.src = value.image;
          selectedImage.alt = value.label;
        } else {
          selectedImage.style.display = 'none';
        }
      }

      if (role === 'size') {
        if (selectedTitle) selectedTitle.textContent = value.label;
        if (selectedDetails) {
          const selected = this.data.selected;
          if (selected && selected.width && selected.height && selected.depth) {
            selectedDetails.innerHTML = `<span><strong>Width</strong> ${selected.width}cm</span> <span><strong>Height</strong> ${selected.height}cm</span> <span><strong>Depth</strong> ${selected.depth}cm</span>`;
          } else if (value.width && value.height && value.depth) {
            selectedDetails.innerHTML = `<span><strong>Width</strong> ${value.width}cm</span> <span><strong>Height</strong> ${value.height}cm</span> <span><strong>Depth</strong> ${value.depth}cm</span>`;
          } else {
            selectedDetails.textContent = '';
          }
        }
      } else if (role === 'fabric') {
        if (selectedTitle) selectedTitle.textContent = value.category || 'Fabric';
        if (selectedDetails) selectedDetails.textContent = value.label;
      } else {
        if (selectedTitle) selectedTitle.textContent = value.label;
        if (selectedDetails) selectedDetails.textContent = '';
      }
    } else {
      selectedInfo.classList.add('hidden');

      if (selectedImage) {
        selectedImage.style.display = 'block';
      }

      if (isOpen) {
        defaultInfo.style.display = 'none';
      } else {
        defaultInfo.style.display = 'block';
        const countElement = defaultInfo.querySelector('.stitching-accordion__count');
        if (countElement && !value) {
          const option = this.getOption(role);
          const count = option ? option.values.length : 0;
          countElement.textContent = role === 'size' ? '' : `${count} options`;
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Fabric tabs                                                          */
  /* ------------------------------------------------------------------ */

  handleTabClick(tabButton) {
    const targetTabId = tabButton.dataset.tabTarget;
    const tabIndex = tabButton.dataset.tabIndex;
    if (!targetTabId) return;

    const isModalTab = tabButton.closest('.stitching-modal');

    if (isModalTab) {
      const modalTabs = isModalTab.querySelectorAll('.stitching-modal__tab');
      const modalPanels = isModalTab.querySelectorAll('.fabric-modal-tab-content');

      modalTabs.forEach((tab) => tab.classList.remove('active'));
      modalPanels.forEach((panel) => {
        panel.classList.remove('active');
        panel.style.display = 'none';
      });

      tabButton.classList.add('active');
      const targetPanel = document.getElementById(targetTabId);
      if (targetPanel) {
        targetPanel.classList.add('active');
        targetPanel.style.display = 'grid';
      }
    } else {
      document.querySelectorAll('.fabric-tabs__nav-item').forEach((tab) => {
        if (!tab.closest('.stitching-modal')) tab.classList.remove('active');
      });
      document.querySelectorAll('.fabric-tabs__panel').forEach((panel) => {
        panel.classList.remove('active');
      });

      tabButton.classList.add('active');
      const targetPanel = document.getElementById(targetTabId);
      if (targetPanel) {
        targetPanel.classList.add('active');
      }
    }

    document.dispatchEvent(new CustomEvent('fabric-tab:change', {
      detail: { tabIndex: parseInt(tabIndex), targetTabId: targetTabId },
      bubbles: true
    }));
  }

  activateSelectedFabricTab() {
    // Open the desktop tab and the modal tab containing the preselected fabric.
    const selectedDesktop = document.querySelector('.fabric-tabs__panel .stitching-option.selected');
    if (selectedDesktop) {
      const panel = selectedDesktop.closest('.fabric-tabs__panel');
      const navItem = document.querySelector(`.fabric-tabs__nav .fabric-tabs__nav-item[data-tab-target="${panel.id}"]`);
      if (navItem && !navItem.classList.contains('active')) {
        this.handleTabClick(navItem);
      }
    }

    const fabricModal = document.getElementById('stitching-modal-fabric');
    if (fabricModal) {
      const selectedModal = fabricModal.querySelector('.fabric-modal-tab-content .stitching-option.selected');
      if (selectedModal) {
        const panel = selectedModal.closest('.fabric-modal-tab-content');
        const navItem = fabricModal.querySelector(`.stitching-modal__tab[data-tab-target="${panel.id}"]`);
        if (navItem && !navItem.classList.contains('active')) {
          this.handleTabClick(navItem);
        }
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Size swiper                                                          */
  /* ------------------------------------------------------------------ */

  initializeSizeSwiper() {
    const sizeSwiperEl = this.container.querySelector('[data-size-swiper]');
    if (!sizeSwiperEl) return;

    this.showOptionsBlock = sizeSwiperEl.getAttribute('data-show-blocks');

    if (typeof Swiper === 'undefined') {
      console.warn('[variant-configurator] Swiper is not loaded yet.');
      return;
    }

    try {
      this.sizeSwiper = new Swiper(sizeSwiperEl, {
        slidesPerView: 'auto',
        spaceBetween: 20,
        freeMode: true,
        grabCursor: true,
        navigation: {
          nextEl: '[data-slider-arrow-next]',
          prevEl: '[data-slider-arrow-prev]'
        },
        on: {
          slideChange: () => this.updateSwiperProgress(),
          progress: () => this.updateSwiperProgress(),
          touchStart: () => this.syncSwiperState(),
          touchMove: () => this.updateSwiperProgress(),
          transitionEnd: () => this.updateSwiperProgress()
        }
      });

      setTimeout(() => {
        this.updateSwiperProgress();
        const selectedSlide = sizeSwiperEl.querySelector('.stitching-option.selected');
        if (selectedSlide) {
          this.updateSwiperToSelectedOption(selectedSlide);
        } else {
          const activeIndex = this.showOptionsBlock === 'true' ? 1 : 0;
          this.sizeSwiper.slideTo(activeIndex, 300, () => {});
        }
      }, 100);

      this.addResizeHandler();
    } catch (error) {
      console.error('[variant-configurator] Failed to initialize Swiper:', error);
    }
  }

  updateSwiperProgress() {
    const progressBar = this.container.querySelector('[data-size-progress]');
    const progressContainer = progressBar?.parentElement;
    if (!progressBar || !progressContainer || !this.sizeSwiper) return;

    const swiperContainer = this.container.querySelector('[data-size-swiper]');
    const swiperWrapper = swiperContainer?.querySelector('.swiper-wrapper');
    if (!swiperContainer || !swiperWrapper) return;

    const containerWidth = swiperContainer.offsetWidth;
    const wrapperWidth = swiperWrapper.scrollWidth;

    if (wrapperWidth <= containerWidth) {
      progressContainer.style.display = 'none';
      return;
    }
    progressContainer.style.display = 'block';

    const rawTranslate = this.sizeSwiper.getTranslate();
    const spaceBetween = 20;
    const maxScrollDistance = wrapperWidth - containerWidth + spaceBetween * 2;

    let progress = 0;
    if (maxScrollDistance > 0 && rawTranslate <= 0) {
      progress = Math.min(Math.abs(rawTranslate) / maxScrollDistance, 1);
    }
    progressBar.style.width = `${progress * 100}%`;
  }

  syncSwiperState() {
    if (!this.sizeSwiper) return;
    this.sizeSwiper.updateSize();
    this.sizeSwiper.updateSlides();
    this.sizeSwiper.updateProgress();
    this.sizeSwiper.updateSlidesProgress();
    const currentTranslate = this.sizeSwiper.getTranslate();
    this.sizeSwiper.setTranslate(currentTranslate);
    this.updateSwiperProgress();
  }

  addResizeHandler() {
    if (!this.sizeSwiper) return;

    const handleResize = () => {
      this.sizeSwiper.update();
      setTimeout(() => {
        this.updateSwiperProgress();
      }, 50);
    };

    if (this.sizeSwiper.resizeHandler) {
      window.removeEventListener('resize', this.sizeSwiper.resizeHandler);
    }
    window.addEventListener('resize', handleResize);
    this.sizeSwiper.resizeHandler = handleResize;
  }

  updateSwiperToSelectedOption(optionElement) {
    if (!this.sizeSwiper) return;
    const slide = optionElement.closest('.swiper-slide');
    if (!slide) return;

    const slideIndex = Array.from(slide.parentNode.children).indexOf(slide);
    if (slideIndex >= 0) {
      this.sizeSwiper.slideTo(slideIndex - 1, 300);
      setTimeout(() => {
        this.updateSwiperProgress();
      }, 300);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Mobile: bar + modals                                                 */
  /* ------------------------------------------------------------------ */

  initializeMobileModals() {
    if (typeof window.CustomModal === 'undefined') {
      console.warn('[variant-configurator] CustomModal not available');
      return;
    }

    this.data.options.forEach((option) => {
      const modal = document.getElementById(`stitching-modal-${option.role}`);
      if (!modal) return;
      this.mobileModals[option.role] = new window.CustomModal(modal, {
        scrollWrapper: '.stitching-modal__wrapper',
        closures: '.js-custom-modal-close',
        bottomSheetHandle: '.js-custom-modal-bottom-sheet-handle'
      }, () => {}, () => {
        this.updateModalSelectedStates(option.role);
      });
    });
  }

  updateModalSelectedStates(role) {
    const modal = document.getElementById(`stitching-modal-${role}`);
    if (!modal) return;

    const selected = this.getSelectedValue(role);
    modal.querySelectorAll('.stitching-modal__option').forEach((option) => {
      const isSelected = selected && option.dataset.optionValueId.toString() === selected.id.toString();
      option.classList.toggle('selected', !!isSelected);
    });
  }

  handleMobileTriggerClick(trigger) {
    const modalTarget = trigger.dataset.modalTarget;
    if (this.mobileModals && this.mobileModals[modalTarget]) {
      this.mobileModals[modalTarget].open();
    }
  }

  updateMobileDisplay() {
    this.data.options.forEach((option) => {
      const role = option.role;
      const bar = document.querySelector(`.stitching-option-mobile--${role}`);
      if (!bar) return;

      const value = this.getSelectedValue(role);
      const header = bar.querySelector('.stitching-option-mobile__header');
      const thumbnail = bar.querySelector(`[data-mobile-${role}-thumbnail]`);
      const title = bar.querySelector(`[data-mobile-${role}-title]`);
      const details = bar.querySelector(`[data-mobile-${role}-details]`);

      if (value) {
        if (thumbnail) {
          if (value.image) {
            thumbnail.innerHTML = `<img src="${value.image}" alt="${value.label}" />`;
            thumbnail.style.display = 'flex';
          } else {
            thumbnail.innerHTML = '';
            thumbnail.style.display = 'none';
          }
        }
        if (title) {
          title.textContent = value.label;
        }
        if (details && role === 'size') {
          const selected = this.data.selected;
          if (selected && selected.width && selected.height && selected.depth) {
            details.textContent = `W${selected.width} x H${selected.height} x D${selected.depth} cm`;
            details.style.display = 'block';
          } else {
            details.textContent = '';
            details.style.display = 'none';
          }
        }
        if (header) {
          const defaultText = header.querySelector('.default-text');
          if (defaultText && header.dataset.changeText) {
            defaultText.textContent = header.dataset.changeText;
          }
        }
        bar.classList.add('stitching-option-mobile--selected');
      } else {
        if (thumbnail) {
          thumbnail.innerHTML = '';
          thumbnail.style.display = 'none';
        }
        if (title) {
          title.textContent = `${option.values.length} options`;
        }
        if (header) {
          const defaultText = header.querySelector('.default-text');
          if (defaultText && header.dataset.defaultText) {
            defaultText.textContent = header.dataset.defaultText;
          }
        }
        bar.classList.remove('stitching-option-mobile--selected');
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Selection sentence                                                   */
  /* ------------------------------------------------------------------ */

  initializeSelectionDisplay() {
    this.selectionDisplayElement = document.querySelector('[data-stitching-selection-display]');
    if (!this.selectionDisplayElement) return;

    this.displayTexts = {
      sizeOnly: this.selectionDisplayElement.dataset.textSizeOnly || '- excellent choice!',
      sizeFabric: this.selectionDisplayElement.dataset.textSizeFabric || '- brilliant look!',
      allOptions: this.selectionDisplayElement.dataset.textAllOptions || '- fantastic style!'
    };

    this.contentSpan = this.selectionDisplayElement.querySelector('[data-stitching-display-content]');
    this.endingSpan = this.selectionDisplayElement.querySelector('[data-stitching-display-ending]');
  }

  updateSelectionDisplay() {
    if (!this.selectionDisplayElement || !this.contentSpan || !this.endingSpan) return;

    const size = this.getSelectedValue('size');
    const fabric = this.getSelectedValue('fabric');
    const leg = this.getSelectedValue('leg');

    // Build from whatever roles the product actually has - no hardcoded
    // placeholder text when a role is absent.
    let contentText = '';
    if (size) contentText = size.label;
    if (fabric) contentText = contentText ? `${contentText} in ${fabric.label}` : fabric.label;
    if (leg) contentText = contentText ? `${contentText} and ${leg.label}` : leg.label;

    let endingText = '';
    if (size && fabric && leg) {
      endingText = this.displayTexts.allOptions;
    } else if (fabric) {
      endingText = this.displayTexts.sizeFabric;
    } else if (contentText) {
      endingText = this.displayTexts.sizeOnly;
    }

    this.contentSpan.textContent = contentText;
    this.endingSpan.textContent = endingText;
  }

  /* ------------------------------------------------------------------ */
  /* Sticky add-to-basket bar                                             */
  /* ------------------------------------------------------------------ */

  updateStickyBar() {
    // The sticky bar SSRs the product featured image, which native-variant
    // products may not have. Keep its thumbnail on the selected variant's
    // first gallery media.
    const media = (this.data.gallery && this.data.gallery[0]) || null;
    if (!media || !media.preview_image) return;

    const wrapper = document.querySelector('[data-sticky-image]');
    if (!wrapper) return;

    let img = wrapper.querySelector('img');
    if (!img) {
      wrapper.innerHTML = '';
      img = document.createElement('img');
      img.width = 140;
      img.height = 140;
      img.loading = 'lazy';
      wrapper.appendChild(img);
    }
    img.src = media.preview_image;
    img.removeAttribute('srcset');
    img.alt = media.alt || this.data.productTitle || '';
  }

  /* ------------------------------------------------------------------ */
  /* Cart line item properties (external script + CRM contract)           */
  /* ------------------------------------------------------------------ */

  generateStitchingSetId() {
    // Same shape as the stitching configurator produced, so the external
    // script and the CRM keep reading the property they already know.
    const timestamp = Date.now();
    const random = Math.random().toString(36).substr(2, 9);
    return `stitching-set-${timestamp}-${random}`;
  }

  getCartProperties() {
    if (!this.stitchingSetId) {
      this.stitchingSetId = this.generateStitchingSetId();
    }

    const properties = { _stitching_set_id: this.stitchingSetId };

    // Fabric category = the swatch category of the selected fabric value,
    // the same strings the fabric group metaobjects used as tab names.
    const fabricCategory = this.getSelectedValue('fabric')?.category;
    if (fabricCategory) {
      properties._fabric_category = fabricCategory;
    }

    return properties;
  }

  setFormProperty(form, name, value) {
    const selector = `input[name="properties[${name}]"]`;

    if (!value) {
      form.querySelector(selector)?.remove();
      return;
    }

    let input = form.querySelector(selector);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${name}]`;
      form.appendChild(input);
    }
    input.value = value;
  }

  syncCartProperties() {
    // Injected from JS rather than rendered in Liquid: the set id has to be
    // generated per add, and the add-to-cart form block is re-rendered on
    // variant change (which would drop server-rendered inputs anyway).
    const form = document.querySelector('product-info form[data-type="add-to-cart-form"]');
    if (!form) return;

    const properties = this.getCartProperties();
    ['_stitching_set_id', '_fabric_category'].forEach((name) => {
      this.setFormProperty(form, name, properties[name]);
    });
  }

  /* ------------------------------------------------------------------ */
  /* Buy button                                                           */
  /* ------------------------------------------------------------------ */

  getSelectedQuantity() {
    // The quantity selector only renders when the theme.quantity metafield
    // enables it; without the input everything is a single unit.
    const quantityInput = document.querySelector('product-info .product-form__quantity .quantity__input');
    if (!quantityInput) return 1;
    const quantity = parseInt(quantityInput.value, 10);
    return quantity > 0 ? quantity : 1;
  }

  getButtonPriceFormatted(selected) {
    const quantity = this.getSelectedQuantity();
    const moneyFormat = window.theme && window.theme.settings && window.theme.settings.money_format;
    if (quantity > 1 && typeof selected.price === 'number' && typeof formatPrice === 'function' && moneyFormat) {
      return formatPrice(moneyFormat, (selected.price * quantity) / 100);
    }
    return selected.priceFormatted;
  }

  updateSubmitButton() {
    // UX parity with the stitching configurator: the add-to-cart button
    // carries the selected variant's price (times quantity when the product
    // renders a quantity selector). Runs after the native toggleSubmitButton,
    // so sold-out/unavailable states stay native.
    const selected = this.data.selected;

    const shopPayButton = document.querySelector('[data-shop-pay-button]');
    if (shopPayButton) {
      shopPayButton.disabled = !selected || !selected.available;
    }

    if (!selected || !selected.available || !selected.priceFormatted) return;

    const button = document.querySelector('product-info form[data-type="add-to-cart-form"] button[name="add"]');
    if (!button || button.hasAttribute('disabled')) return;

    const span = button.querySelector('span');
    if (!span) return;

    span.textContent = `Add to basket – ${this.getButtonPriceFormatted(selected)}`;
  }

  initShopPayButton() {
    // Mirrors the stitching flow: buy-buttons renders the button hidden and
    // disabled; the configurator reveals it. Adds the selected variant and
    // heads to the cart, where the Shop Pay CTA takes over.
    const shopPayButton = document.querySelector('[data-shop-pay-button]');
    if (!shopPayButton) return;

    shopPayButton.style.display = '';
    shopPayButton.disabled = !this.data.selected || !this.data.selected.available;

    // Back navigation from the cart restores the page from bfcache with the
    // is-loading state still on the button - reset it on every pageshow.
    window.addEventListener('pageshow', () => {
      shopPayButton.classList.remove('is-loading');
      shopPayButton.disabled = !this.data.selected || !this.data.selected.available;
    });

    shopPayButton.addEventListener('click', async () => {
      const selected = this.data.selected;
      if (!selected || !selected.available) return;

      shopPayButton.classList.add('is-loading');
      shopPayButton.disabled = true;

      try {
        // Respect the PDP quantity selector when the product renders one
        // (theme.quantity metafield); otherwise fall back to a single unit.
        const quantity = this.getSelectedQuantity();

        const formData = new FormData();
        formData.append('items[0][id]', selected.id);
        formData.append('items[0][quantity]', quantity);
        Object.entries(this.getCartProperties()).forEach(([name, value]) => {
          formData.append(`items[0][properties][${name}]`, value);
        });

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          body: formData
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Failed to add to cart');
        }

        window.location.href = '/cart';
      } catch (error) {
        console.error('[variant-configurator] Shop Pay add to cart failed', error);
        shopPayButton.classList.remove('is-loading');
        shopPayButton.disabled = false;
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Gallery                                                              */
  /* ------------------------------------------------------------------ */

  isMobile() {
    return window.innerWidth < 750;
  }

  isGalleryReady() {
    const galleryElement = document.querySelector('swiper-product-gallery');
    const thumbsElement = document.querySelector('swiper-product-thumbs');

    if (this.isMobile()) {
      return galleryElement && galleryElement.instance && galleryElement.instance.initialized;
    }
    return galleryElement && thumbsElement;
  }

  addMobileBannerImages(mediaArray) {
    if (!this.isMobile()) return mediaArray;

    const banners = this.data.mobileBanners;
    if (!banners || banners.length === 0) return mediaArray;

    return [...banners, ...mediaArray];
  }

  updateGalleryFromData(retryCount = 0) {
    const gallery = this.data.gallery;
    if (!gallery || !Array.isArray(gallery) || gallery.length === 0) return;

    if (this.isMobile() && !this.isGalleryReady() && retryCount < 5) {
      setTimeout(() => {
        this.updateGalleryFromData(retryCount + 1);
      }, 200);
      return;
    }

    const mediaToShow = this.addMobileBannerImages(gallery);
    this.replaceGalleryMedia(mediaToShow);
    this.replaceCustomGridMedia(gallery);
  }

  replaceGalleryMedia(newMediaArray) {
    const galleryElement = document.querySelector('swiper-product-gallery');
    const thumbsElement = document.querySelector('swiper-product-thumbs');

    if (!galleryElement || !galleryElement.instance) return;

    const gallerySwiper = galleryElement.instance;
    const thumbsSwiper = thumbsElement ? thumbsElement.instance : null;

    gallerySwiper.allowTouchMove = false;
    if (thumbsSwiper) thumbsSwiper.allowTouchMove = false;

    try {
      gallerySwiper.removeAllSlides();
      if (thumbsSwiper) thumbsSwiper.removeAllSlides();

      newMediaArray.forEach((media, index) => {
        const gallerySlide = this.createGallerySlide(media, index);
        const thumbSlide = this.createThumbSlide(media, index);

        gallerySwiper.appendSlide(gallerySlide);
        if (thumbsSwiper && thumbSlide) {
          thumbsSwiper.appendSlide(thumbSlide);
        }
      });

      gallerySwiper.update();
      if (thumbsSwiper) thumbsSwiper.update();

      gallerySwiper.slideTo(0, 0);
      if (thumbsSwiper) thumbsSwiper.slideTo(0, 0);

      setTimeout(() => {
        gallerySwiper.allowTouchMove = true;
        if (thumbsSwiper) thumbsSwiper.allowTouchMove = true;
      }, 100);

      this.reinitializePhotoSwipe(galleryElement);
      this.reinitializeModelViewers(galleryElement);
    } catch (error) {
      console.error('[variant-configurator] Error updating gallery media:', error);
      gallerySwiper.allowTouchMove = true;
      if (thumbsSwiper) thumbsSwiper.allowTouchMove = true;
    }
  }

  createGallerySlide(media, index) {
    const slideDiv = document.createElement('div');
    slideDiv.className = 'swiper-slide';
    slideDiv.setAttribute('data-media-id', media.id);
    slideDiv.setAttribute('data-media-type', media.media_type);
    slideDiv.setAttribute('data-index', index);

    const mediaDiv = document.createElement('div');
    mediaDiv.className = 'media';

    let mediaContent = '';

    switch (media.media_type) {
      case 'video':
        if (media.video_tag) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(media.video_tag, 'text/html');
          const videoElement = doc.querySelector('video');
          if (videoElement) {
            mediaContent = videoElement.outerHTML;
          }
        }
        break;

      case 'external_video':
        if (media.external_id && media.host) {
          mediaContent = this.createExternalVideoHTML(media);
        }
        break;

      case 'model':
        if (media.model_viewer_tag) {
          const modelContainer = document.createElement('div');
          modelContainer.className = 'model-viewer-container';

          const modelOverlay = document.createElement('div');
          modelOverlay.className = 'model-overlay';

          const modelBtn = document.createElement('div');
          modelBtn.className = `media__type--badge main-product__media--feat color-${this.getGalleryColorScheme()} gradient model-viewer-btn`;
          modelBtn.title = 'Model';

          const modelInner = document.createElement('div');
          modelInner.className = 'media__model--inner';
          modelInner.innerHTML = `
            ${this.getModelIcon()}
            <span>Drag to spin</span>
          `;

          modelBtn.appendChild(modelInner);
          modelOverlay.appendChild(modelBtn);
          mediaDiv.appendChild(modelOverlay);

          const parser = new DOMParser();
          const doc = parser.parseFromString(media.model_viewer_tag, 'text/html');
          const modelElement = doc.querySelector('model-viewer');
          if (modelElement) {
            modelContainer.appendChild(modelElement);
          }

          mediaContent = modelContainer.outerHTML;
        }
        break;

      case 'image':
      default:
        if (media.preview_image) {
          mediaContent = this.createImageHTML(media, index);
        }
        break;
    }

    if (media.media_type === 'image' && media.preview_image) {
      const overlay = this.createZoomOverlay(media, index);
      mediaDiv.appendChild(overlay);
    }

    mediaDiv.innerHTML += mediaContent;
    slideDiv.appendChild(mediaDiv);

    return slideDiv;
  }

  createThumbSlide(media, index) {
    if (!media.preview_image) return null;

    const slideDiv = document.createElement('div');
    slideDiv.className = 'swiper-slide';
    slideDiv.setAttribute('data-media-id', media.id);
    slideDiv.setAttribute('data-media-type', media.media_type);
    slideDiv.setAttribute('data-index', index);

    const mediaDiv = document.createElement('div');
    mediaDiv.className = 'media media--thumb';

    if (media.media_type !== 'image') {
      const overlay = document.createElement('div');
      overlay.className = 'media__overlay';

      const badge = document.createElement('div');
      badge.className = 'media__type--badge main-product__media--feat';
      badge.title = media.media_type.charAt(0).toUpperCase() + media.media_type.slice(1);

      if (media.media_type === 'model') {
        badge.innerHTML = this.getModelIcon();
      } else if (media.media_type === 'video' || media.media_type === 'external_video') {
        badge.innerHTML = this.getVideoIcon();
      }

      overlay.appendChild(badge);
      mediaDiv.appendChild(overlay);
    }

    const img = document.createElement('img');
    img.src = media.preview_image;
    img.alt = media.alt || '';
    img.loading = 'lazy';
    img.width = 128;
    img.height = 128;

    mediaDiv.appendChild(img);
    slideDiv.appendChild(mediaDiv);

    return slideDiv;
  }

  createImageHTML(media, mediaIndex = 0) {
    const picture = document.createElement('picture');

    const baseImageUrl = media.preview_image;
    if (!baseImageUrl) return '';

    const createImageUrl = (size, scale = 1) => {
      if (scale === 2) {
        return baseImageUrl.replace(/_\d+x(\.\w+)(\?.*)?$/, `_${size}x${scale}$1$2`);
      }
      return baseImageUrl.replace(/_\d+x(\.\w+)(\?.*)?$/, `_${size}x$1$2`);
    };

    const sources = [
      { media: '(min-width: 749px)', size: '800' },
      { media: '(min-width: 600px)', size: '749' },
      { media: '(min-width: 500px)', size: '600' },
      { media: '(min-width: 400px)', size: '500' },
      { media: '(min-width: 390px)', size: '400' },
      { media: null, size: '390' }
    ];

    sources.forEach((sourceConfig) => {
      const source = document.createElement('source');
      const srcset1x = createImageUrl(sourceConfig.size, 1);
      const srcset2x = createImageUrl(sourceConfig.size, 2);
      source.srcset = `${srcset1x} 1x, ${srcset2x} 2x`;
      if (sourceConfig.media) {
        source.media = sourceConfig.media;
      }
      picture.appendChild(source);
    });

    const img = document.createElement('img');
    img.className = 'responsive-wrapper__image';
    img.src = createImageUrl('800', 1);
    img.alt = media.alt || '';

    if (media.preview_image_width) img.width = media.preview_image_width;
    if (media.preview_image_height) img.height = media.preview_image_height;

    if (mediaIndex === 0) {
      img.loading = 'eager';
      img.setAttribute('fetchpriority', 'high');
    } else {
      img.loading = 'lazy';
    }

    picture.appendChild(img);

    return picture.outerHTML;
  }

  createExternalVideoHTML(media) {
    let embedUrl = '';
    if (media.host === 'youtube') {
      embedUrl = `https://www.youtube.com/embed/${media.external_id}?enablejsapi=1`;
    } else if (media.host === 'vimeo') {
      embedUrl = `https://player.vimeo.com/video/${media.external_id}`;
    }

    if (embedUrl) {
      return `<iframe src="${embedUrl}" width="100%" height="100%" frameborder="0" allowfullscreen></iframe>`;
    }
    return '';
  }

  createZoomOverlay(media, index) {
    const overlay = document.createElement('div');
    overlay.className = 'media__overlay';

    const opener = document.createElement('div');
    opener.className = `product__gallery-opener main-product__media--feat main-product__media--feat-gallery no-js-hidden color-${this.getGalleryColorScheme()} gradient`;
    opener.setAttribute('data-modal-opener-id', `image-${media.id}`);

    const link = document.createElement('a');
    link.href = media.preview_image;
    link.className = 'product__gallery-toggle motion-reduce';
    link.setAttribute('type', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('data-media-id', media.id);
    link.setAttribute('data-pswp-image', '');
    link.setAttribute('data-pswp-src', media.preview_image);
    link.setAttribute('data-pswp-width', media.preview_image_width || '');
    link.setAttribute('data-pswp-height', media.preview_image_height || '');
    link.setAttribute('target', '_blank');

    if (media.srcset) {
      link.setAttribute('data-pswp-srcset', media.srcset);
    }

    if (index > 0) {
      link.setAttribute('tabindex', '-1');
    }

    link.innerHTML = this.getExpandIcon();

    const visuallyHidden = document.createElement('span');
    visuallyHidden.className = 'visually-hidden';
    visuallyHidden.textContent = `Open media ${index + 1}`;
    link.appendChild(visuallyHidden);

    opener.appendChild(link);
    overlay.appendChild(opener);

    return overlay;
  }

  replaceCustomGridMedia(newMediaArray) {
    const customGridElement = document.querySelector('.main-product__media--custom-grid');
    if (!customGridElement) return;

    try {
      customGridElement.innerHTML = '';

      const firstMediaType = newMediaArray.length > 0 ? newMediaArray[0].media_type : null;
      const hideSecondImage = this.data.hideSecondImage === true;

      newMediaArray.forEach((media, index) => {
        let isLargeItem;
        if (hideSecondImage) {
          isLargeItem = index === 0;
        } else {
          isLargeItem = index < 2;
        }

        const gridItem = this.createCustomGridItem(media, index, isLargeItem, firstMediaType);
        customGridElement.appendChild(gridItem);
      });

      this.reinitializePhotoSwipeForGrid(customGridElement);
      this.reinitializeModelViewersForGrid(customGridElement);
    } catch (error) {
      console.error('[variant-configurator] Error updating custom grid media:', error);
    }
  }

  createCustomGridItem(media, index, isLargeItem, firstMediaType = null) {
    const gridItem = document.createElement('div');
    gridItem.className = `main-product__media--grid-item${isLargeItem ? ' main-product__media--grid-item-large' : ' main-product__media--grid-item-small'} color-${this.getGalleryColorScheme()}`;

    const mediaCard = this.createMediaCardForGrid(media, index, firstMediaType);
    gridItem.appendChild(mediaCard);

    return gridItem;
  }

  createMediaCardForGrid(media, index, firstMediaType = null) {
    const slideDiv = document.createElement('div');
    slideDiv.className = 'swiper-slide';
    slideDiv.setAttribute('data-media-id', media.id);
    slideDiv.setAttribute('data-media-type', media.media_type);
    slideDiv.setAttribute('data-index', index);

    const mediaDiv = document.createElement('div');
    mediaDiv.className = 'media';

    let mediaContent = '';

    switch (media.media_type) {
      case 'video':
        if (media.video_tag) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(media.video_tag, 'text/html');
          const videoElement = doc.querySelector('video');
          if (videoElement) {
            mediaContent = videoElement.outerHTML;
          }
        }
        break;

      case 'external_video':
        if (media.external_id && media.host) {
          mediaContent = this.createExternalVideoHTML(media);
        }
        break;

      case 'model':
        if (media.model_viewer_tag) {
          const modelContainer = document.createElement('div');
          modelContainer.className = 'model-viewer-container';

          const modelOverlay = document.createElement('div');
          modelOverlay.className = 'model-overlay';

          const modelBtn = document.createElement('div');
          modelBtn.className = `media__type--badge main-product__media--feat color-${this.getGalleryColorScheme()} gradient model-viewer-btn`;
          modelBtn.title = 'Model';

          const modelInner = document.createElement('div');
          modelInner.className = 'media__model--inner';
          modelInner.innerHTML = `
            ${this.getModelIcon()}
            <span>Drag to spin</span>
          `;

          modelBtn.appendChild(modelInner);
          modelOverlay.appendChild(modelBtn);
          mediaDiv.appendChild(modelOverlay);

          const parser = new DOMParser();
          const doc = parser.parseFromString(media.model_viewer_tag, 'text/html');
          const modelElement = doc.querySelector('model-viewer');
          if (modelElement) {
            modelContainer.appendChild(modelElement);
          }

          mediaContent = modelContainer.outerHTML;
        }
        break;

      case 'image':
      default:
        if (media.preview_image) {
          mediaContent = this.createImageHTML(media, index);
        }
        break;
    }

    if (media.media_type === 'image' && media.preview_image) {
      const overlay = this.createZoomOverlayForGrid(media, index, firstMediaType);
      mediaDiv.appendChild(overlay);
    }

    mediaDiv.innerHTML += mediaContent;
    slideDiv.appendChild(mediaDiv);

    return slideDiv;
  }

  createZoomOverlayForGrid(media, index, firstMediaType = null) {
    const overlay = document.createElement('div');
    overlay.className = 'media__overlay';

    let hideGalleryToggle = false;
    if (index === 0) {
      hideGalleryToggle = false;
    } else if (index === 1) {
      hideGalleryToggle = firstMediaType !== 'model';
    }

    const opener = document.createElement('div');
    let openerClasses = `product__gallery-opener main-product__media--feat main-product__media--feat-gallery no-js-hidden color-${this.getGalleryColorScheme()} gradient`;
    if (hideGalleryToggle) {
      openerClasses += ' hidden';
    }
    opener.className = openerClasses;
    opener.setAttribute('data-modal-opener-id', `image-${media.id}`);

    const link = document.createElement('a');
    link.href = media.preview_image;
    link.className = 'product__gallery-toggle motion-reduce';
    link.setAttribute('type', 'button');
    link.setAttribute('aria-haspopup', 'dialog');
    link.setAttribute('data-media-id', media.id);
    link.setAttribute('data-pswp-image', '');
    link.setAttribute('data-pswp-src', media.preview_image);
    link.setAttribute('data-pswp-width', media.preview_image_width || '');
    link.setAttribute('data-pswp-height', media.preview_image_height || '');
    link.setAttribute('target', '_blank');

    if (media.srcset) {
      link.setAttribute('data-pswp-srcset', media.srcset);
    }

    if (index > 0) {
      link.setAttribute('tabindex', '-1');
    }

    link.innerHTML = this.getExpandIcon();

    const visuallyHidden = document.createElement('span');
    visuallyHidden.className = 'visually-hidden';
    visuallyHidden.textContent = `Open media ${index + 1}`;
    link.appendChild(visuallyHidden);

    opener.appendChild(link);
    overlay.appendChild(opener);

    return overlay;
  }

  reinitializePhotoSwipe(galleryElement) {
    if (galleryElement && galleryElement.photoSwipeLightboxInit && typeof galleryElement.photoSwipeLightboxInit === 'function') {
      galleryElement.photoSwipeLightboxInit();
    } else {
      this.reinitializePhotoSwipeForSingleMedia();
    }
  }

  reinitializePhotoSwipeForSingleMedia() {
    if (typeof initSingleMediaPhotoSwipe === 'function') {
      setTimeout(() => {
        initSingleMediaPhotoSwipe();
      }, 100);
    } else {
      setTimeout(() => {
        window.dispatchEvent(new Event('resize'));
      }, 100);
    }
  }

  reinitializePhotoSwipeForGrid(gridElement) {
    if (typeof PhotoSwipeLightbox !== 'undefined') {
      const singleMediaContainer = document.querySelector('[data-single-media]');
      if (singleMediaContainer && typeof window.initSingleMediaPhotoSwipe === 'function') {
        setTimeout(() => {
          window.initSingleMediaPhotoSwipe();
        }, 100);
      } else {
        setTimeout(() => {
          window.dispatchEvent(new Event('resize'));
        }, 100);
      }
    }
  }

  reinitializeModelViewers(galleryElement) {
    if (galleryElement.initModelViewer && typeof galleryElement.initModelViewer === 'function') {
      galleryElement.initModelViewer();
    }
    if (typeof initAllGridModelViewers === 'function') {
      setTimeout(() => {
        initAllGridModelViewers();
      }, 100);
    }
  }

  reinitializeModelViewersForGrid(gridElement) {
    if (typeof initAllGridModelViewers === 'function') {
      setTimeout(() => {
        initAllGridModelViewers();
      }, 100);
    }
  }

  getGalleryColorScheme() {
    if (this.data && this.data.galleryColorScheme) {
      return this.data.galleryColorScheme;
    }
    const existingGallery = document.querySelector('swiper-product-gallery');
    if (existingGallery) {
      const colorMatch = existingGallery.className.match(/color-([^\s]+)/);
      if (colorMatch) return colorMatch[1];
    }
    return 'background-1';
  }

  getModelIcon() {
    return `<svg class="media__model--icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2 10C2 10 2.12132 9.15076 5.63604 5.63604C9.15076 2.12132 14.8492 2.12132 18.364 5.63604C19.6092 6.88131 20.4133 8.40072 20.7762 10M2 10V4M2 10H8M22 14C22 14 21.8787 14.8492 18.364 18.364C14.8492 21.8787 9.15076 21.8787 5.63604 18.364C4.39076 17.1187 3.58669 15.5993 3.22383 14M22 14V20M22 14H16" stroke="#8C7C6D" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }

  getVideoIcon() {
    return `<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-play" viewBox="0 0 20 20">
      <path d="M8.5 5.5L15 10l-6.5 4.5V5.5z" fill="currentColor"/>
    </svg>`;
  }

  getExpandIcon() {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8.16667 5.83333L12.25 1.75M12.25 1.75H8.75M12.25 1.75V5.25M5.83333 8.16667L1.75 12.25M1.75 12.25H5.25M1.75 12.25L1.75 8.75" stroke="#8C7C6D" stroke-width="0.875" stroke-linecap="round" stroke-linejoin="round"/>
    </svg>`;
  }
}

// Contact-info modal wiring for the optional info slides (mirrors the
// stitching implementation, only bound when the elements exist).
function initVariantConfiguratorInfoSlides() {
  const additionalInfoSlides = document.querySelectorAll('[data-additional-slide]');
  const infoModals = document.querySelectorAll('[data-info-modal]');
  if (additionalInfoSlides.length === 0 || infoModals.length === 0) return;

  const closeModal = (infoModal) => {
    if (!infoModal) return;
    infoModal.classList.remove('open');
    const inner = infoModal.querySelector('.contact-info-modal__inner');
    if (inner) inner.classList.remove('animated');
  };

  // Nearest ancestor that CONTAINS a modal wins (the desktop slide's modal
  // lives in its own .size-swiper-container). A flat querySelector over the
  // configurator returned the mobile bar's modal, which is display:none on
  // desktop - the modal "opened" invisibly.
  const findModalForSlide = (slide) => {
    let parent = slide.parentElement;
    while (parent && parent !== document.body) {
      const modal = parent.querySelector('[data-info-modal]');
      if (modal) return modal;
      parent = parent.parentElement;
    }
    return infoModals[0];
  };

  additionalInfoSlides.forEach((slide) => {
    slide.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const infoModal = findModalForSlide(slide);
      const inner = infoModal ? infoModal.querySelector('.contact-info-modal__inner') : null;
      if (!infoModal || !inner) return;

      infoModals.forEach((m) => {
        if (m !== infoModal) closeModal(m);
      });

      infoModal.style.zIndex = '99999';
      infoModal.classList.add('open');
      setTimeout(() => {
        inner.classList.add('animated');
      }, 0);
    });
  });

  infoModals.forEach((infoModal) => {
    const inner = infoModal.querySelector('.contact-info-modal__inner');
    if (!inner) return;

    infoModal.querySelectorAll('[data-close-modal]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        closeModal(infoModal);
      });
    });

    infoModal.addEventListener('click', (event) => {
      if (event.target === infoModal && !inner.contains(event.target)) {
        closeModal(infoModal);
      }
    });
  });
}

function initVariantConfigurator() {
  const container = document.querySelector('[data-variant-configurator]');
  const data = document.getElementById('variant-configurator-data');

  if (!container || !data) return;

  if (typeof Swiper !== 'undefined') {
    window.variantConfigurator = new VariantConfigurator();
    initVariantConfiguratorInfoSlides();
  } else {
    setTimeout(initVariantConfigurator, 100);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initVariantConfigurator);
} else {
  initVariantConfigurator();
}

window.VariantConfigurator = VariantConfigurator;
